// Pengirim. Concept lines (JEV ui.region_gate, all kept):
//   snd_header  Job: name the screen and count the senders. Message: Pengirim,
//               n active and n revoked. Action: none. Container: plain spacing.
//   snd_list    Job: see who may send data, reveal a phone, revoke or restore
//               a sender. Message: label, status, phone (masked), paired date,
//               submission count. Action: Cabut akses or Pulihkan, behind an
//               AlertDialog (JEV ui.component_recipe confirm: the JAL Core
//               dialog spec). Container: rows, recipe core.table from 1024
//               (one hairline header row, column rules), stacked rows below.
// Hand-written inside the kit Section: the kit has no record list composition.
import { Fragment, useId, useState } from "react";
import { Section } from "@bot-whatsapp/ui";
import { isUnauthorized, useDashboardApi } from "./api";
import { useDelayed, useOnUnauthorized, useResource } from "./data";
import { formatDate, formatNumber } from "./format";
import { useReveal } from "./reveal";
import type { Sender, SenderFilter } from "./types";
import {
  ConfirmDialog,
  EmptyState,
  FilterChips,
  LoadError,
  MaskedValue,
  Notice,
  RevealButton,
  RowsSkeleton,
  ScreenHeader,
  StatusBadge,
  type ChipOption,
  type ConfirmRequest,
} from "./ui";

const FILTERS: ChipOption<SenderFilter>[] = [
  { value: "active", label: "Aktif" },
  { value: "revoked", label: "Dicabut" },
  { value: "all", label: "Semua" },
];

const EMPTY: Record<SenderFilter, { title: string; body: string }> = {
  active: { title: "Belum ada pengirim aktif", body: "Pengirim muncul di sini setelah mengirim kode pairing ke nomor bot." },
  revoked: { title: "Tidak ada pengirim yang dicabut", body: "Pengirim yang aksesnya dicabut tampil di sini dan bisa dipulihkan kapan saja." },
  all: { title: "Belum ada pengirim", body: "Buat kode pairing, lalu minta pengirim mengirim angka kodenya ke nomor bot." },
};

export function SendersScreen() {
  const api = useDashboardApi();
  const onUnauthorized = useOnUnauthorized();
  const headingId = useId();
  const [status, setStatus] = useState<SenderFilter>("active");
  const reveal = useReveal((id) => api.revealSender(id));
  const [confirm, setConfirm] = useState<ConfirmRequest | null>(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState<string | null>(null);

  const list = useResource(() => api.listSenders(status), [api, status]);
  // The header counts both groups whatever the filter shows.
  const counts = useResource(() => api.listSenders("all"), [api, list.refreshedAt]);
  const slow = useDelayed(list.loading && !list.data);

  const all = counts.data?.items ?? [];
  const active = all.filter((s) => s.revokedAt === null).length;
  const meta = counts.data
    ? `${formatNumber(active)} aktif, ${formatNumber(all.length - active)} dicabut. Pengirim yang dicabut tidak bisa mengirim data sampai dipulihkan.`
    : "Memuat pengirim.";

  function ask(s: Sender) {
    setConfirmError(null);
    if (s.revokedAt === null) {
      setConfirm({
        title: `Cabut akses ${s.label}?`,
        body: `Nomor ${s.phoneMasked} tidak bisa mengirim data lagi sampai dipulihkan. Kiriman lamanya tetap tersimpan.`,
        confirmLabel: "Cabut akses",
        run: () => api.revokeSender(s.id).then(() => undefined),
      });
    } else {
      setConfirm({
        title: `Pulihkan ${s.label}?`,
        body: `Nomor ${s.phoneMasked} bisa langsung mengirim nama dan NIK ke bot lagi.`,
        confirmLabel: "Pulihkan akses",
        tone: "primary",
        run: () => api.restoreSender(s.id).then(() => undefined),
      });
    }
  }

  async function runConfirm() {
    if (!confirm || confirmBusy) return;
    setConfirmBusy(true);
    setConfirmError(null);
    try {
      await confirm.run();
      setConfirm(null);
      list.reload();
    } catch (err) {
      if (isUnauthorized(err)) onUnauthorized();
      setConfirmError("Perubahan belum tersimpan. Coba lagi sebentar lagi.");
    } finally {
      setConfirmBusy(false);
    }
  }

  const items = list.data?.items ?? [];

  return (
    <Section id="pengirim" className="dash-screen" composition="custom" variant="rows" labelledBy={headingId}>
      <ScreenHeader id={headingId} title="Pengirim" meta={meta} />
      <section className="dash-region kit-full" aria-label="Daftar pengirim">
        <FilterChips label="Status pengirim" value={status} options={FILTERS} onChange={setStatus} />
        {list.error && !list.data ? (
          <LoadError what="Pengirim" onRetry={list.reload} />
        ) : !list.data ? (
          slow ? <RowsSkeleton /> : null
        ) : items.length === 0 ? (
          <EmptyState
            title={EMPTY[status].title}
            body={EMPTY[status].body}
            action={
              status === "revoked" ? undefined : (
                <a className="btn btn-secondary" href="#kode">
                  Buat kode pairing
                </a>
              )
            }
          />
        ) : (
          <>
            <div className="dash-table-box">
              <table className="dash-table" aria-label="Pengirim">
                <thead>
                  <tr>
                    <th scope="col">Label</th>
                    <th scope="col" className="dash-col-status">Status</th>
                    <th scope="col" className="dash-col-phone">Nomor</th>
                    <th scope="col" className="dash-col-date">
                      {status === "revoked" ? "Dicabut" : status === "active" ? "Terpasang" : "Sejak"}
                    </th>
                    <th scope="col" className="dash-col-count dash-end">
                      Kiriman
                    </th>
                    <th scope="col" className="dash-col-actions">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((s) => {
                    const shown = reveal.shown.has(s.id);
                    const full = shown ? reveal.values.get(s.id)?.phone : undefined;
                    const error = reveal.errors.get(s.id);
                    const revoked = s.revokedAt !== null;
                    return (
                      <Fragment key={s.id}>
                      <tr data-revealed={shown || undefined} aria-busy={reveal.loading.has(s.id) || undefined}>
                        <td className="dash-strong">{s.label}</td>
                        <td>
                          <StatusBadge tone={revoked ? "danger" : "success"}>{revoked ? "Dicabut" : "Aktif"}</StatusBadge>
                        </td>
                        <td className="dash-nowrap">
                          <MaskedValue kind="phone" full={full} masked={s.phoneMasked} />
                        </td>
                        <td className="dash-nowrap dash-time">{formatDate(s.revokedAt ?? s.pairedAt)}</td>
                        <td className="dash-nowrap kit-num dash-end">{formatNumber(s.submissionCount)}</td>
                        <td className="dash-cell-action">
                          <span className="dash-cell-buttons">
                            <RevealButton shown={shown} loading={reveal.loading.has(s.id)} onToggle={() => reveal.toggle(s.id)} subject={s.label} />
                            <button
                              type="button"
                              className={revoked ? "btn-secondary dash-restore" : "btn-secondary dash-danger-quiet dash-restore"}
                              onClick={() => ask(s)}
                            >
                              {revoked ? "Pulihkan" : "Cabut akses"}
                            </button>
                          </span>
                        </td>
                      </tr>
                      {error ? (
                        <tr className="dash-error-row">
                          <td colSpan={6}>
                            <Notice tone="danger" title="Data tidak dibuka">
                              {error}
                            </Notice>
                          </td>
                        </tr>
                      ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <ul className="dash-rows dash-stack">
              {items.map((s) => {
                const shown = reveal.shown.has(s.id);
                const full = shown ? reveal.values.get(s.id)?.phone : undefined;
                const error = reveal.errors.get(s.id);
                const revoked = s.revokedAt !== null;
                return (
                  <li key={s.id} className="dash-row dash-sender" data-revealed={shown || undefined} aria-busy={reveal.loading.has(s.id) || undefined}>
                    <div className="dash-sender-head">
                      <p className="dash-row-title">{s.label}</p>
                      <StatusBadge tone={revoked ? "danger" : "success"}>{revoked ? "Dicabut" : "Aktif"}</StatusBadge>
                    </div>
                    <dl className="dash-pairs dash-sender-pairs">
                      <div>
                        <dt>Nomor</dt>
                        <dd>
                          <MaskedValue kind="phone" full={full} masked={s.phoneMasked} />
                        </dd>
                      </div>
                      <div>
                        <dt>{revoked ? "Dicabut" : "Terpasang"}</dt>
                        <dd className="dash-time">{formatDate(s.revokedAt ?? s.pairedAt)}</dd>
                      </div>
                      <div>
                        <dt>Kiriman</dt>
                        <dd className="kit-num">{formatNumber(s.submissionCount)}</dd>
                      </div>
                    </dl>
                    <div className="dash-sender-actions">
                      <RevealButton shown={shown} loading={reveal.loading.has(s.id)} onToggle={() => reveal.toggle(s.id)} subject={s.label} />
                      <button type="button" className={revoked ? "btn-secondary" : "btn-secondary dash-danger-quiet"} onClick={() => ask(s)}>
                        {revoked ? "Pulihkan" : "Cabut akses"}
                      </button>
                    </div>
                    {error ? (
                      <Notice tone="danger" title="Data tidak dibuka">
                        {error}
                      </Notice>
                    ) : null}
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </section>
      <ConfirmDialog
        request={confirm}
        busy={confirmBusy}
        error={confirmError}
        onConfirm={runConfirm}
        onClose={() => setConfirm(null)}
      />
    </Section>
  );
}
